import React, { useEffect, useState } from "react";
import { Star, Users, Lightbulb, Clock, Trophy, Sparkles, Award, Heart, Rocket, Target, Search, X } from "lucide-react";
import api, { BACKEND_URL } from "@/lib/api";

export const BADGE_ICONS = { star: Star, users: Users, lightbulb: Lightbulb, clock: Clock, trophy: Trophy, sparkles: Sparkles, award: Award, heart: Heart, rocket: Rocket, target: Target };
export const BADGE_COLORS = {
  amber: "from-amber-400 to-yellow-300", sky: "from-sky-500 to-cyan-400", violet: "from-violet-500 to-fuchsia-400",
  emerald: "from-emerald-500 to-teal-400", orange: "from-orange-500 to-rose-400", pink: "from-pink-500 to-rose-400",
};

export const photoSrc = (p) => (!p ? "" : p.startsWith("http") ? p : `${BACKEND_URL}${p}`);

export const Avatar = ({ name = "?", photo, size = "w-9 h-9", ring = "" }) => {
  const initials = name.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  return (
    <span className={`${size} ${ring} ${size.includes("rounded") ? "" : "rounded-full"} bg-gradient-to-br from-sky-500 to-indigo-500 text-white font-bold flex items-center justify-center overflow-hidden shrink-0 text-sm`}>
      {photo ? <img src={photoSrc(photo)} alt="" className="w-full h-full object-cover" /> : initials}
    </span>
  );
};

export const BadgeTile = ({ icon, color, size = "w-14 h-14" }) => {
  const Icon = BADGE_ICONS[icon] || Award;
  return <span className={`${size} rounded-2xl bg-gradient-to-br ${BADGE_COLORS[color] || BADGE_COLORS.orange} flex items-center justify-center shadow-md shrink-0`}><Icon className="w-1/2 h-1/2 text-white" strokeWidth={2.2} /></span>;
};

// Page title card (matches the header style used across growth pages).
export const PageCard = ({ Icon, title, subtitle, actions, testid }) => (
  <div className="bg-white rounded-3xl border border-slate-100 shadow-sm px-6 py-5 mb-5 flex flex-wrap items-center gap-4" data-testid={testid}>
    <Icon className="w-9 h-9 text-sky-500" strokeWidth={1.8} />
    <div className="flex-1 min-w-0">
      <h1 className="font-display font-black text-3xl text-slate-900 tracking-tight">{title}</h1>
      {subtitle && <p className="text-sm text-slate-500 mt-0.5">{subtitle}</p>}
    </div>
    {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
  </div>
);

export const PillBtn = ({ primary, children, ...p }) => (
  <button {...p} className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-colors disabled:opacity-50 ${primary ? "bg-sky-600 text-white hover:bg-sky-700 shadow" : "border border-slate-200 text-sky-700 hover:bg-sky-50 bg-white"}`}>{children}</button>
);

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
export const defaultRange = (days = 30) => { const e = new Date(); const s = new Date(); s.setDate(s.getDate() - days); return { start: ymd(s), end: ymd(e) }; };

export const DateRange = ({ value, onChange, testid = "date-range" }) => (
  <div className="flex items-center gap-1.5 border border-slate-200 rounded-xl px-3 h-11 bg-white" data-testid={testid}>
    <input type="date" value={value.start} max={value.end} onChange={(e) => onChange({ ...value, start: e.target.value })} className="text-sm bg-transparent outline-none" data-testid={`${testid}-start`} />
    <span className="text-slate-400 text-sm">to</span>
    <input type="date" value={value.end} min={value.start} onChange={(e) => onChange({ ...value, end: e.target.value })} className="text-sm bg-transparent outline-none" data-testid={`${testid}-end`} />
    {(value.start || value.end) && <button onClick={() => onChange({ start: "", end: "" })} title="All time" className="text-slate-400 hover:text-slate-700" data-testid={`${testid}-clear`}><X className="w-4 h-4" /></button>}
  </div>
);

export const SearchBox = ({ value, onChange, testid }) => (
  <div className="relative">
    <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
    <input value={value} onChange={(e) => onChange(e.target.value)} placeholder="Search" className="h-11 pl-10 pr-3 rounded-xl border border-slate-200 text-sm w-64 outline-none focus:ring-2 focus:ring-sky-200" data-testid={testid} />
  </div>
);

export const EmptyState = ({ Icon, title, text, action }) => (
  <div className="py-16 text-center" data-testid="empty-state">
    <Icon className="w-14 h-14 text-slate-300 mx-auto mb-3" strokeWidth={1.4} />
    <div className="font-bold text-slate-800">{title}</div>
    <div className="text-sm text-slate-500 mt-1 mb-4">{text}</div>
    {action}
  </div>
);

// Multi-select of members (admin sending tokens / recognitions).
export const MemberPicker = ({ picked, setPicked }) => {
  const [members, setMembers] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/growth/members").then((r) => setMembers(r.data)).catch(() => {}); }, []);
  const shown = members.filter((m) => `${m.name} ${m.email} ${m.role_label}`.toLowerCase().includes(q.toLowerCase())).slice(0, 60);
  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  return (
    <div>
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, email or role…" className="input-pill w-full mb-2" data-testid="member-picker-search" />
      <div className="max-h-48 overflow-y-auto border border-slate-100 rounded-xl divide-y divide-slate-50">
        {shown.map((m) => (
          <label key={m.user_id} className="flex items-center gap-2.5 px-3 py-2 cursor-pointer hover:bg-slate-50 text-sm" data-testid={`member-option-${m.user_id}`}>
            <input type="checkbox" checked={picked.includes(m.user_id)} onChange={() => toggle(m.user_id)} className="accent-sky-600" />
            <span className="flex-1 min-w-0"><span className="block truncate">{m.name}</span><span className="block text-[11px] text-slate-400 truncate">{m.email}</span></span><span className="text-xs text-slate-400 shrink-0">{m.role_label}</span>
          </label>
        ))}
      </div>
      <div className="text-xs text-slate-500 mt-1" data-testid="member-picker-count">{picked.length} selected</div>
    </div>
  );
};

export const Modal = ({ title, onClose, children, testid, wide }) => (
  <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
    <div className={`bg-white rounded-2xl shadow-2xl w-full ${wide ? "max-w-3xl" : "max-w-lg"} max-h-[90vh] overflow-y-auto p-6`} onClick={(e) => e.stopPropagation()} data-testid={testid}>
      <div className="flex items-center justify-between mb-4"><h3 className="font-display font-black text-xl">{title}</h3><button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid={`${testid}-close`}><X className="w-5 h-5" /></button></div>
      {children}
    </div>
  </div>
);
